import React, { useMemo, useState } from 'react';
import { Users, MapPin, Calendar, Package, Flame, Wrench, FileSignature } from 'lucide-react';

const parseDate = (data) => {
  if (!data || typeof data !== 'string') return null;

  if (data.includes('-')) {
    const dt = new Date(data);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  if (data.includes('/')) {
    const [datePart] = data.split(' ');
    const [dia, mes, ano] = datePart.split('/');
    const dt = new Date(`${ano}-${mes}-${dia}`);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
};

const formatarValor = (valor) => {
  const numero = Number(valor || 0);
  if (!Number.isFinite(numero)) return 'R$ 0';

  if (numero >= 1000000) return `R$ ${(numero / 1000000).toFixed(1)}M`;
  if (numero >= 1000) return `R$ ${(numero / 1000).toFixed(0)}K`;
  return `R$ ${numero.toFixed(0)}`;
};

const formatarData = (data) => {
  if (!data) return 'N/A';

  try {
    if (data.includes('-')) {
      const dateObj = new Date(data);
      return dateObj
        .toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        .replace('.', '');
    }

    if (data.includes('/')) {
      const [datePart] = data.split(' ');
      const [dia, mes, ano] = datePart.split('/');
      const dateObj = new Date(ano, mes - 1, dia);
      return dateObj
        .toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
        .replace('.', '');
    }

    return data;
  } catch (error) {
    return data;
  }
};

const calcularDiasEmAberto = (data) => {
  const dt = parseDate(data);
  if (!dt) return null;

  const hoje = new Date();
  const diffMs = hoje.getTime() - dt.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const getRiscoClasse = (dias) => {
  if (dias === null) return 'bg-slate-100 text-slate-700 border-slate-300/40';
  if (dias >= 10) return 'bg-[color:var(--status-danger-chip)] text-[color:var(--status-danger-strong)] border-[color:var(--status-danger-border)]';
  if (dias >= 6) return 'bg-[color:var(--status-warning-chip)] text-[color:var(--status-warning-strong)] border-[color:var(--status-warning-border)]';
  return 'bg-[color:var(--status-good-chip)] text-[color:var(--status-good-strong)] border-[color:var(--status-good-border)]';
};

const getCardBorderClass = (dias) => {
  if (dias === null) return 'border-slate-200/50 hover:border-slate-300';
  if (dias >= 10) return 'border-[color:var(--status-danger-border)] hover:border-[color:var(--status-danger-border)]';
  if (dias >= 6) return 'border-[color:var(--status-warning-border)] hover:border-[color:var(--status-warning-border)]';
  return 'border-[color:var(--status-good-border)] hover:border-[color:var(--status-good-border)]';
};

const ListaClientesOsAberta72h = ({ clientes = [], clientesAssinatura = [] }) => {
  const [tipoListaAtiva, setTipoListaAtiva] = useState('instalacao');
  const semDadosGerais = (!Array.isArray(clientes) || clientes.length === 0)
    && (!Array.isArray(clientesAssinatura) || clientesAssinatura.length === 0);

  const clientesDaListaAtiva = useMemo(() => (
    tipoListaAtiva === 'assinatura'
      ? (Array.isArray(clientesAssinatura) ? clientesAssinatura : [])
      : (Array.isArray(clientes) ? clientes : [])
  ), [tipoListaAtiva, clientes, clientesAssinatura]);

  const clientesOrdenados = useMemo(() => {
    return [...clientesDaListaAtiva].sort((a, b) => {
      const dataA = parseDate(a?.data);
      const dataB = parseDate(b?.data);
      if (!dataA && !dataB) return 0;
      if (!dataA) return 1;
      if (!dataB) return -1;
      return dataA.getTime() - dataB.getTime();
    });
  }, [clientesDaListaAtiva]);

  const totalReceita = useMemo(
    () => clientesOrdenados.reduce((acc, item) => acc + Number(item?.valor || 0), 0),
    [clientesOrdenados]
  );

  const mediaDias = useMemo(() => {
    const dias = clientesOrdenados
      .map((cliente) => calcularDiasEmAberto(cliente.data))
      .filter((valor) => valor !== null);

    if (!dias.length) return 0;
    return Math.round(dias.reduce((acc, valor) => acc + valor, 0) / dias.length);
  }, [clientesOrdenados]);

  if (semDadosGerais) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-[color:var(--status-danger-border)] bg-[color:var(--surface)] p-6 shadow-sm h-full">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_100%_0%,rgba(239,68,68,0.12),transparent_58%)]" />
        <h3 className="relative text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center tracking-tight">
          <Flame className="h-5 w-5 text-[color:var(--status-danger-strong)] mr-2" />
          {tipoListaAtiva === 'assinatura' ? 'Clientes de Assinatura' : 'Clientes com OS >72h'}
        </h3>
        <div className="relative flex flex-col items-center justify-center text-[color:var(--text-muted)] py-8 h-full">
          <div className="rounded-2xl bg-[color:var(--surface-muted)] border border-[color:var(--border)] p-4 mb-3">
            <Users className="h-10 w-10 text-[color:var(--text-muted)]" strokeWidth={1.3} />
          </div>
          <p className="font-semibold text-[color:var(--text-primary)]">
            {tipoListaAtiva === 'assinatura'
              ? 'Nenhum cliente de assinatura encontrado'
              : 'Nenhum cliente com OS >72h encontrado'}
          </p>
          <p className="text-sm mt-1 text-center max-w-xs">
            {tipoListaAtiva === 'assinatura'
              ? 'Esta seção exibe clientes da lista de assinatura.'
              : 'Esta seção exibe clientes com OS aberta por período acima do esperado.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[color:var(--status-danger-border)] bg-[color:var(--surface)] p-4 sm:p-5 shadow-sm h-full">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(110%_80%_at_100%_0%,rgba(239,68,68,0.13),transparent_50%)]" />

      <div className="relative mb-4 sm:mb-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center tracking-tight">
            <Flame className="h-5 w-5 text-[color:var(--status-danger-strong)] mr-2" />
            {tipoListaAtiva === 'assinatura' ? 'Clientes de assinatura' : 'Clientes com OS >72h'}
          </h3>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-slate-100 p-1.5 shadow-sm dark:border-[color:var(--border)] dark:bg-[color:var(--surface-muted)]">
              <button
                type="button"
                onClick={() => setTipoListaAtiva('instalacao')}
                aria-pressed={tipoListaAtiva === 'instalacao'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 ${
                  tipoListaAtiva === 'instalacao'
                    ? 'bg-[color:var(--status-good-strong)] text-white border border-[color:var(--status-good-border)] shadow-sm dark:bg-[color:var(--status-good-chip)] dark:text-[color:var(--status-good-strong)] dark:border-[color:var(--status-good-border)] dark:shadow-[0_6px_16px_rgba(16,185,129,0.2)]'
                    : 'text-slate-700 border border-transparent hover:text-slate-900 hover:bg-[color:var(--surface)] dark:text-[color:var(--text-muted)] dark:hover:text-[color:var(--text-primary)] dark:hover:bg-[color:var(--surface)]'
                }`}
              >
                <Wrench className="h-3.5 w-3.5" />
                Instalação
              </button>
              <button
                type="button"
                onClick={() => setTipoListaAtiva('assinatura')}
                aria-pressed={tipoListaAtiva === 'assinatura'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 ${
                  tipoListaAtiva === 'assinatura'
                    ? 'bg-fuchsia-700 text-white border border-fuchsia-800 shadow-sm dark:bg-fuchsia-500/12 dark:text-fuchsia-300 dark:border-fuchsia-300/30 dark:shadow-[0_6px_16px_rgba(217,70,239,0.2)]'
                    : 'text-slate-700 border border-transparent hover:text-slate-900 hover:bg-[color:var(--surface)] dark:text-[color:var(--text-muted)] dark:hover:text-[color:var(--text-primary)] dark:hover:bg-[color:var(--surface)]'
                }`}
              >
                <FileSignature className="h-3.5 w-3.5" />
                Assinatura
              </button>
            </div>
            <span className="text-xs sm:text-sm rounded-full border border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-chip)] px-2.5 py-1 font-semibold text-[color:var(--status-danger-strong)]">
              {clientesOrdenados.length} clientes
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">Tempo médio aberto</p>
            <p className="mt-0.5 font-semibold text-[color:var(--status-warning-strong)]">{mediaDias} dias</p>
          </div>
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">Receita dos planos</p>
            <p className="mt-0.5 font-semibold text-[color:var(--status-danger-strong)]">{formatarValor(totalReceita)}</p>
          </div>
        </div>
      </div>

      <div className="relative space-y-2.5 max-h-[390px] sm:max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
        {!clientesOrdenados.length && (
          <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-4 text-sm text-[color:var(--text-muted)] text-center">
            {tipoListaAtiva === 'assinatura'
              ? 'Nenhum cliente de assinatura para o período selecionado.'
              : 'Nenhum cliente de instalação para o período selecionado.'}
          </div>
        )}
        {clientesOrdenados.map((cliente, index) => {
          const diasEmAberto = calcularDiasEmAberto(cliente.data);
          const riscoClasse = getRiscoClasse(diasEmAberto);

          return (
            <div
              key={cliente.id || index}
              className={`rounded-xl border ${getCardBorderClass(diasEmAberto)} bg-[color:var(--surface-muted)] p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(239,68,68,0.14)]`}
            >
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                <div className="min-w-0">
                  <div className="mb-2">
                    <span className="font-semibold text-[color:var(--text-primary)] break-words leading-tight text-sm sm:text-[15px]">
                      {cliente.nome}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-1.5 text-xs sm:text-[13px] text-[color:var(--text-muted)]">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-[color:var(--status-good-strong)] shrink-0" />
                      <span className="truncate" title={cliente.cidade}>
                        {cliente.cidade || 'Cidade não informada'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-[color:var(--status-danger-strong)] shrink-0" />
                      <span>{formatarData(cliente.data)}</span>
                    </div>

                    {cliente.vendedor && (
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-sky-500 shrink-0" />
                        <span className="truncate" title={cliente.vendedor}>
                          {cliente.vendedor}
                        </span>
                      </div>
                    )}

                    {cliente.plano && (
                      <div className="flex items-center gap-1.5">
                        <Package className="h-3.5 w-3.5 text-[color:var(--accent)] shrink-0" />
                        <span className="truncate" title={cliente.plano}>
                          {cliente.plano}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="min-w-[120px] sm:min-w-[132px] flex flex-col items-end gap-1.5">
                  <span className="inline-flex justify-center rounded-lg bg-[color:var(--status-good-chip)] px-2.5 py-1 text-xs font-semibold text-[color:var(--status-good-strong)] whitespace-nowrap">
                    {formatarValor(cliente.valor)}
                  </span>
                  {diasEmAberto !== null && (
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${riscoClasse}`}>
                      {diasEmAberto}d em aberto
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(148, 163, 184, 0.2); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.65); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(148, 163, 184, 0.9); }
      `}</style>
    </div>
  );
};

export default ListaClientesOsAberta72h;
